import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth0 } from '@auth0/auth0-react';
import { CheckCircle2, Loader2 } from 'lucide-react';

import { Button } from '@/ui/design-system/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/ui/design-system/components/ui/dialog';

import { startSlideshow } from '@/utils/api/slideshow/api';
import type {
    ReportArtifact,
    SlideshowEvent,
    SlideshowPreview,
} from '@/utils/api/slideshow/types';
import { downloadFile } from '@/utils/downloadFile';
import {
    trackReportDownloadFailed,
    trackReportDownloadStarted,
    trackReportDownloaded,
} from '@/ui/views/Reports/reportAnalytics';

const PPTX_MIME =
    'application/vnd.openxmlformats-officedocument.presentationml.presentation';

// V1 flow: generate the deck and download it directly — no slide-preview modal,
// no deck-style choice (editorial mode is hidden until V2). Opening the dialog
// with a report starts generation immediately and the .pptx downloads on its
// own when ready; a short "done" state offers a manual re-download as a fallback
// (programmatic downloads can be blocked).
type Stage = 'configure' | 'streaming' | 'done';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** The report's id, for analytics. */
    reportId?: string;
    /** The AI Report to export, passed by the Reports view's "Export to
     * PowerPoint" button. */
    report?: ReportArtifact;
}

const SlideshowExportDialog = ({
    open,
    onOpenChange,
    reportId,
    report,
}: Props) => {
    const { getAccessTokenSilently } = useAuth0();

    const [stage, setStage] = useState<Stage>('configure');
    const [progress, setProgress] = useState<{
        message: string;
        pct: number;
    } | null>(null);
    const [preview, setPreview] = useState<SlideshowPreview | null>(null);
    const abortRef = useRef<AbortController | null>(null);
    const startedRef = useRef(false); // guard the auto-start effect against re-fires

    useEffect(() => {
        if (!open) {
            abortRef.current?.abort();
            abortRef.current = null;
            startedRef.current = false;
            setStage('configure');
            setProgress(null);
            setPreview(null);
        }
    }, [open]);

    // The deck arrives inline (base64) in the draft_ready event — decode it to a
    // Blob and download from memory. No second request, nothing stored server-side.
    const triggerDownload = useCallback((pv: SlideshowPreview) => {
        if (!pv.pptx_base64) return;
        try {
            const bytes = Uint8Array.from(atob(pv.pptx_base64), (c) =>
                c.charCodeAt(0),
            );
            const blob = new Blob([bytes], { type: PPTX_MIME });
            downloadFile(blob, pv.download_filename || 'report.pptx');
        } catch (err) {
            console.error('Download failed:', err);
            toast.error('Download failed.');
        }
    }, []);

    const handleGenerate = useCallback(
        async (artifact: ReportArtifact) => {
            setStage('streaming');
            setProgress({ message: 'Starting…', pct: 0 });
            setPreview(null);

            const widgetCount =
                artifact.config?.widgets?.length ??
                artifact.saved?.config?.widgets?.length ??
                0;
            trackReportDownloadStarted({ reportId, widgetCount });

            const controller = new AbortController();
            abortRef.current = controller;
            try {
                const token = await getAccessTokenSilently();
                await startSlideshow(
                    // Editorial mode is hidden until V2 — always briefed.
                    { mode: 'briefed', report: artifact },
                    token,
                    (event: SlideshowEvent) => {
                        if (event.type === 'progress') {
                            setProgress({
                                message: event.message,
                                pct: event.pct,
                            });
                        } else if (event.type === 'draft_ready') {
                            setPreview(event.preview);
                            setStage('done');
                            triggerDownload(event.preview); // download directly
                            trackReportDownloaded({
                                reportId,
                                widgetCount,
                                slideCount: event.preview.slides.length,
                            });
                        } else if (event.type === 'error') {
                            toast.error(event.message);
                            trackReportDownloadFailed({
                                reportId,
                                widgetCount,
                                reason: event.message,
                            });
                            onOpenChange(false);
                        }
                    },
                    controller.signal,
                );
            } catch (err) {
                if ((err as Error).name === 'AbortError') return;
                console.error('Slideshow export failed:', err);
                toast.error('Slideshow export failed');
                trackReportDownloadFailed({
                    reportId,
                    widgetCount,
                    reason: (err as Error).message,
                });
                onOpenChange(false);
            }
        },
        [getAccessTokenSilently, triggerDownload, onOpenChange, reportId],
    );

    // Production path: opened with a report → go straight to generate + download.
    useEffect(() => {
        if (open && report && !startedRef.current) {
            startedRef.current = true;
            void handleGenerate(report);
        }
    }, [open, report, handleGenerate]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Export to PowerPoint</DialogTitle>
                    <DialogDescription>
                        Generate and download an editable .pptx deck. Slide
                        headlines and commentary are AI-generated and labeled in
                        the deck.
                    </DialogDescription>
                </DialogHeader>

                {stage === 'streaming' && (
                    <StreamingStage progress={progress} />
                )}
                {stage === 'done' && preview && (
                    <DoneStage
                        preview={preview}
                        onDownloadAgain={() => triggerDownload(preview)}
                        onClose={() => onOpenChange(false)}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

// ---------- Generating ---------------------------------------------------------

const StreamingStage = ({
    progress,
}: {
    progress: { message: string; pct: number } | null;
}) => (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <div className="text-sm text-card-foreground">
            {progress?.message ?? 'Working…'}
        </div>
        {progress && (
            <div className="w-64 h-2 bg-muted rounded-full overflow-hidden">
                <div
                    className="h-full bg-primary transition-all"
                    style={{
                        width: `${Math.min(100, Math.round(progress.pct * 100))}%`,
                    }}
                />
            </div>
        )}
    </div>
);

// ---------- Done (auto-downloaded) ---------------------------------------------

const DoneStage = ({
    preview,
    onDownloadAgain,
    onClose,
}: {
    preview: SlideshowPreview;
    onDownloadAgain: () => void;
    onClose: () => void;
}) => {
    const slideCount = preview.slides.length;
    return (
        <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
            <CheckCircle2 className="h-7 w-7 text-primary" />
            <div className="text-sm font-medium text-card-foreground">
                Your deck downloaded
                {slideCount
                    ? ` — ${slideCount} slide${slideCount === 1 ? '' : 's'}`
                    : ''}
                .
            </div>
            <div className="text-xs text-muted-foreground">
                Check your downloads folder. Didn&rsquo;t get it?
            </div>
            <div className="flex gap-2 pt-1">
                <Button variant="secondary" onClick={onDownloadAgain}>
                    Download again
                </Button>
                <Button onClick={onClose}>Done</Button>
            </div>
        </div>
    );
};

export default SlideshowExportDialog;
